import { useMemo, useState } from "react";
import {
  EndPanel,
  EventLog,
  GameShell,
  GameTable,
  HelpPanel,
  TableSettings,
} from "../components";
import type { ShellNav } from "../components";
import { useI18n, usePlayerNames } from "../i18n";
import type { StringKey } from "@kikik/i18n";
import type { RoomView } from "@kikik/game-protocol";
import { computeWaits } from "../game/tableModel";
import { useOnlineGame } from "../game/useOnlineGame";

export interface OnlineModeProps {
  /** Overridable for tests; defaults to the same origin as the page. */
  url?: string;
  autoConnect?: boolean;
  /** Supplied by the app shell; absent when a test renders the mode directly. */
  nav?: ShellNav;
}

/** Connection states are protocol values; only their labels are translated. */
const STATUS_KEY: Record<string, StringKey> = {
  idle: "online.status.idle",
  connecting: "online.status.connecting",
  online: "online.status.online",
  offline: "online.status.offline",
  error: "online.status.error",
  reconnecting: "online.status.reconnecting",
};

interface RoomPanelProps {
  room: RoomView;
  compact: boolean;
  isHost: boolean;
  onAddBot: () => void;
  onStart: () => void;
  onLeave: () => void;
  connected: boolean;
}

/**
 * The room: seats, codes and who is connected.
 *
 * Before the game starts it is the whole screen, because there is no table
 * yet. Once the table exists it moves into the menu, because a list of four
 * seat rows is not worth a quarter of the felt those seats are sitting at.
 */
function RoomPanel({ room, compact, isHost, onAddBot, onStart, onLeave, connected }: RoomPanelProps) {
  const { t, seat: seatWind } = useI18n();
  return (
    <section
      className={`panel room ${compact ? "room--compact" : ""}`}
      data-testid="online-room"
    >
      <header className="room__header">
        <h3>
          {t("online.roomTitle")} <span className="room__code">{room.code}</span>
        </h3>
        <div className="room__actions">
          {isHost && !room.started ? (
            <>
              <button type="button" className="button button--ghost" onClick={onAddBot} disabled={!connected}>
                {t("online.addBot")}
              </button>
              <button type="button" className="button button--primary" onClick={onStart} disabled={!connected}>
                {t("online.start")}
              </button>
            </>
          ) : null}
          <button type="button" className="button button--ghost" onClick={onLeave} disabled={!connected}>
            {t("online.leave")}
          </button>
        </div>
      </header>
      <ul className="room__seats" aria-label={t("online.seatList")}>
        {room.seats.map((seat) => (
          <li key={seat.seat} className={`room__seat room__seat--${seat.occupant}`}>
            <strong>{t("online.seat", { number: seat.seat + 1 })}</strong>
            {seat.wind ? <span className="muted"> ({seatWind(seat.wind)})</span> : null}
            <span>
              {seat.occupant === "empty"
                ? ` ${t("online.seat.empty")}`
                : ` ${seat.displayName ?? t("online.defaultName")}${seat.you ? ` ${t("online.seat.you")}` : ""}`}
            </span>
            {seat.host ? <span className="badge">{t("online.badge.host")}</span> : null}
            {seat.occupant === "human" && !seat.connected ? (
              <span className="badge badge--claim">{t("online.badge.disconnected")}</span>
            ) : null}
          </li>
        ))}
      </ul>
      {!room.started ? <p className="muted">{t("online.waitingHost")}</p> : null}
    </section>
  );
}

export function OnlineMode({ url, autoConnect, nav }: OnlineModeProps) {
  const { t, tm } = useI18n();
  const names = usePlayerNames();
  const game = useOnlineGame({
    ...(url ? { url } : {}),
    ...(autoConnect === undefined ? {} : { autoConnect }),
    defaultName: t("online.defaultName"),
  });
  const [codeInput, setCodeInput] = useState("");

  const nameFor = names.table(
    game.view?.you ?? 0,
    (playerId) => game.room?.seats[playerId]?.displayName ?? null,
  );
  const waits = useMemo(() => (game.model ? computeWaits(game.model) : []), [game.model]);
  const inRoom = game.room !== null;
  const playing = game.model !== null;
  const isHost = game.room?.seats.some((seat) => seat.you && seat.host) ?? false;
  const statusKey = STATUS_KEY[game.status];

  const room = game.room ? (
    <RoomPanel
      room={game.room}
      compact={playing}
      isHost={isHost}
      onAddBot={game.addBot}
      onStart={game.startGame}
      onLeave={game.leaveRoom}
      connected={game.status === "online"}
    />
  ) : null;

  const shell = {
    mode: "online" as const,
    onMode: nav?.onMode ?? ((): void => {}),
    onExit: nav?.onExit ?? ((): void => {}),
  };

  /*
   * The connection pill is the one piece of state allowed in the bar: unlike
   * the turn or the wall it is *not* readable from the table, and a player who
   * has silently dropped needs to know before they wonder why nothing moves.
   */
  const barStatus = (
    <>
      {playing && game.room ? (
        <span className="gamebar__room" data-testid="bar-room-code">
          <span className="gamebar__room-label">{t("online.roomTitle")}</span>
          <span className="room__code">{game.room.code}</span>
        </span>
      ) : null}
      <span className={`pill pill--${game.status}`} data-status={game.status}>
        {statusKey ? t(statusKey) : game.status}
      </span>
    </>
  );

  const reconnect =
    inRoom &&
    game.status !== "online" &&
    game.status !== "connecting" &&
    game.status !== "reconnecting" ? (
      <button type="button" className="button button--ghost" onClick={game.connect}>
        {t("online.reconnect")}
      </button>
    ) : null;

  const notice = game.notice ? (
    <p className="notice" role="alert">
      {tm(game.notice, { playerName: nameFor })}
      <button type="button" className="notice__close" onClick={game.clearNotice}>
        {t("notice.close")}
      </button>
    </p>
  ) : null;

  const overlay = game.model?.result ? (
    <EndPanel
      result={game.model.result}
      you={game.model.you}
      nameFor={nameFor}
      {...(isHost ? { onNewGame: game.startGame, newGameLabel: t("online.newRound") } : {})}
      onRestart={game.leaveRoom}
      restartLabel={t("online.leaveRoom")}
    />
  ) : null;

  const menu = (
    <>
      <section className="menu-section">
        <h3 className="sheet__heading">{t("online.title")}</h3>
        <p className="muted menu-section__note">{t("online.subtitle")}</p>
        {reconnect}
      </section>
      {playing ? room : null}
      <EventLog entries={game.log} nameFor={nameFor} />
      <HelpPanel />
      <TableSettings />
    </>
  );

  // Lobby and room-before-start are not a table yet, so they get the page stage.
  if (!playing) {
    return (
      <GameShell
        {...shell}
        variant="page"
        menu={menu}
        barStatus={barStatus}
        {...(notice ? { notice } : {})}
      >
        <div className="page">
          <header className="page__head">
            <h2>{t("online.title")}</h2>
            <p className="muted">{t("online.subtitle")}</p>
            {reconnect}
          </header>

          {!inRoom ? (
            <section className="panel lobby" data-testid="online-lobby">
              <h3>{t("online.lobby.title")}</h3>
              <div className="lobby__row">
                <label htmlFor="mp-name">{t("online.name")}</label>
                <input
                  id="mp-name"
                  value={game.displayName}
                  placeholder={t("online.defaultName")}
                  onChange={(event) => game.setDisplayName(event.target.value)}
                  aria-label={t("online.nameAria")}
                />
              </div>
              <div className="lobby__row">
                <button
                  type="button"
                  className="button button--primary"
                  onClick={game.createRoom}
                  disabled={game.status !== "online"}
                >
                  {t("online.createRoom")}
                </button>
                {game.status !== "online" ? (
                  <button type="button" className="button button--ghost" onClick={game.connect}>
                    {t("online.reconnect")}
                  </button>
                ) : null}
              </div>
              <form
                className="lobby__row"
                onSubmit={(event) => {
                  event.preventDefault();
                  game.joinRoom(codeInput);
                }}
              >
                <label htmlFor="mp-code">{t("online.roomCode")}</label>
                <input
                  id="mp-code"
                  value={codeInput}
                  onChange={(event) => setCodeInput(event.target.value)}
                  placeholder="ABCD"
                  maxLength={4}
                  autoCapitalize="characters"
                  aria-label={t("online.roomCode")}
                />
                <button
                  type="submit"
                  className="button button--ghost"
                  disabled={game.status !== "online" || codeInput.trim().length !== 4}
                >
                  {t("online.join")}
                </button>
              </form>
              <p className="muted">{t("online.lobbyHint")}</p>
            </section>
          ) : (
            room
          )}
        </div>
        {overlay}
      </GameShell>
    );
  }

  return (
    <GameShell
      {...shell}
      menu={menu}
      barStatus={barStatus}
      {...(notice ? { notice } : {})}
      {...(overlay ? { overlay } : {})}
    >
      {game.model ? (
        <GameTable
          model={game.model}
          nameFor={nameFor}
          waits={waits}
          hintTileId={null}
          onSubmit={game.submit}
          manualDraw={false}
          statusExtra={t("online.roomExtra", { code: game.room?.code ?? "" })}
          ceremonyKey={`${game.room?.code ?? ""}:${game.gameSerial}`}
        />
      ) : null}
    </GameShell>
  );
}
